// ─── MDD Phenotype Grading Engine ─────────────────────────────────────────────
//
// Based on research synthesis:
//   - Simmons et al. 2020 — Appetite-direction endocrine/metabolic/immune subtypes
//   - Simmons et al. 2016 — Reward vs interoceptive neurocircuitry by appetite
//   - Cerit et al. 2019 — Ghrelin-brain associations in hyperphagic vs hypophagic MDD
//   - Piccolo et al. 2023 — Resting-state OFC/insula differences in appetite phenotypes
//   - Pizzagalli 2014 — Anhedonia as endophenotype, stress-DA pathway
//   - Xie et al. 2021 — Medial/lateral OFC and reward/nonreward sensitivity
//   - Ionescu et al. 2015 — Treatment-resistant depression, phenotype-treatment mismatch
//   - Vahratian et al. 2021 — PHQ-4 validation as screening tool
//
// This is a guide-level grading system, not a diagnostic tool.
// The science is emerging — scores reflect signal strength, not certainty.
// ──────────────────────────────────────────────────────────────────────────────

// ─── Input types ──────────────────────────────────────────────────────────────

export interface PhenotypeInput {
  // PHQ-4 (0–3 each, most recent week)
  phq4?: {
    q1: number;  // interest/pleasure (anhedonia)
    q2: number;  // feeling down/depressed
    q3: number;  // nervous/anxious/on edge
    q4: number;  // can't stop worrying
  };

  // Lifestyle pillars (0–3 each, most recent week)
  pillars?: {
    diet: number;       // 0=Poor, 1=Fair, 2=Good, 3=Excellent
    stress: number;     // 0=Overwhelmed, 1=Struggling, 2=Managing, 3=Thriving
    substances: number; // 0=Problematic ... 3=None/Min
    social: number;     // 0=Isolated ... 3=Thriving
  };

  // Thought & behavior patterns (optional — from RRS/PSWQ/BADS/HINT brief items)
  checkin?: {
    rumScore: number;       // 0–9 — rumination composite (higher = more)
    badsAvoidance: number;  // 0–3 — behavioral avoidance item (higher = more)
    badsActivation: number; // 0–6 — behavioral activation composite (higher = better)
    hintScore: number;      // 0–9 — habitual neg thinking composite (higher = more)
  };

  // Structured appetite field (0=Much less, 1=Somewhat less, 2=Normal, 3=Increased)
  appetite?: number;

  // 30-day weight change in lbs (positive = gain, negative = loss)
  weightDelta?: number;

  // Symptoms (string array from weekly check-in)
  symptoms?: string[];

  // Lab biomarkers (most recent values)
  labs?: {
    // Existing markers
    hsCRP?: number;        // mg/L — inflammation
    insulin?: number;      // uIU/mL — metabolic
    glucose?: number;      // mg/dL — metabolic
    hba1c?: number;        // % — metabolic
    cortisol?: number;     // ug/dL — HPA axis
    testosterone?: number; // ng/dL — hormonal
    vitaminD?: number;     // ng/mL — general health
    tsh?: number;          // mIU/L — thyroid
    // IMHC Tier 1 — Foundational Clinical Labs
    freeT4?: number;        // ng/dL — thyroid function (rule-out hypothyroid mood symptoms)
    b12?: number;           // pg/mL — cognitive and fatigue overlap
    folate?: number;        // ng/mL — cognitive/methylation (interpret with B12)
    ferritin?: number;      // ng/mL — iron stores; interpret with hsCRP for inflammation context
    // IMHC Tier 2 — Metabolic & Inflammatory Phenotype Layer
    triglycerides?: number; // mg/dL — used for TyG index (insulin resistance proxy)
    omega3Index?: number;   // % EPA+DHA — anti-inflammatory status marker
  };

  // Wearable averages (most recent 7 days)
  wearables?: {
    avgHRV?: number;          // ms — autonomic tone
    avgSleepScore?: number;   // 0–100
    avgStressScore?: number;  // 0–100 (Oura stress resilience, inverted)
    avgReadiness?: number;    // 0–100
  };

  // Body composition (from Apple Health — smart scale + Apple Watch)
  bodyComposition?: {
    bmi?: number;              // kg/m² — Body Mass Index
    bodyFat?: number;          // percentage (e.g. 25.0)
    vo2Max?: number;           // mL/kg/min — cardiorespiratory fitness
    bodyFatDelta?: number;     // 30d change in percentage points
    vo2MaxDelta?: number;      // 30d change in mL/kg/min
  };
}

// ─── Output types ─────────────────────────────────────────────────────────────

export interface PhenotypeSignal {
  key: string;
  label: string;
  value: string | number | null;
  direction: 'elevated' | 'reduced' | 'normal' | 'unknown';
  weight: number;  // 0–1 contribution to this phenotype
  source: string;  // data source label
}

export interface PhenotypeResult {
  id: string;
  name: string;
  shortName: string;
  score: number;         // 0–100 grading (signal strength)
  confidence: number;    // 0–100 based on data availability
  level: 'strong' | 'moderate' | 'mild' | 'minimal';
  color: string;
  description: string;
  signals: PhenotypeSignal[];
  clinicalNotes: string[];
}

export interface PhenotypeProfile {
  primary: PhenotypeResult | null;
  results: PhenotypeResult[];
  dataCompleteness: number;  // 0–100
  missingData: string[];
  screeningLevel: 'normal' | 'mild' | 'moderate' | 'severe';
  phq4Score: number;
  lastUpdated: string;
}

// ─── Thresholds (evidence-based) ──────────────────────────────────────────────

const THRESHOLDS = {
  // hsCRP: Simmons et al. 2020 — MDD appetite↑ group had significantly elevated CRP
  // Population median ~1.0 mg/L, >3.0 = high risk, >1.0 = elevated
  hsCRP_elevated: 1.5,
  hsCRP_high: 3.0,

  // Insulin: fasting insulin >8 suggests early insulin resistance
  // Simmons et al. 2020 — appetite↑ MDD had higher insulin and HOMA-IR
  insulin_elevated: 8,
  insulin_high: 12,

  // Glucose: fasting >100 = prediabetic range
  glucose_elevated: 100,
  glucose_high: 126,

  // HbA1c: >5.7 = prediabetic
  hba1c_elevated: 5.7,

  // Cortisol: AM cortisol reference 6–23 ug/dL
  // Simmons et al. 2020 — appetite↓ MDD had significantly elevated cortisol
  cortisol_elevated: 20,
  cortisol_low: 6,

  // HRV: lower HRV = reduced parasympathetic tone
  // <30ms RMSSD = low; <50 = moderate; >65 = good
  hrv_low: 30,
  hrv_moderate: 50,

  // Sleep: Oura 0–100
  sleep_poor: 60,
  sleep_fair: 74,

  // ─── IMHC Tier 1: Foundational Clinical Labs ───────────────────────────────

  // TSH: Normal 0.4–4.0 mIU/L; >2.5 is a functional/integrative threshold for mood impact
  tsh_subclinical: 2.5,    // functional threshold — mood/fatigue symptoms possible
  tsh_elevated: 4.0,       // overt hypothyroid range (rule-out first)

  // Free T4: Normal 0.8–1.8 ng/dL; low = hypothyroid
  freeT4_low: 0.8,

  // B12: Functional deficiency threshold higher than lab standard
  // <300 pg/mL = frank deficiency; <500 = suboptimal for neural function
  b12_deficient: 300,
  b12_low: 500,

  // Folate: <4 ng/mL = deficient; <2 = frank deficiency
  folate_low: 4.0,
  folate_deficient: 2.0,

  // Ferritin: <30 ng/mL = low-normal (iron deficiency); <15 = frank deficiency
  // Elevated ferritin (>200) alongside elevated hsCRP = acute phase reaction
  ferritin_deficient: 15,
  ferritin_low: 30,
  ferritin_elevated: 200,  // acute phase reactant when co-elevated with CRP

  // Vitamin D: <20 ng/mL = deficient; 20–30 = insufficient; >40 = optimal
  vitaminD_deficient: 20,
  vitaminD_insufficient: 30,

  // ─── IMHC Tier 2: Metabolic & Inflammatory ────────────────────────────────

  // Triglycerides: Normal <150 mg/dL (used for TyG index)
  triglycerides_elevated: 150,

  // TyG Index = ln(fasting TG [mg/dL] × fasting glucose [mg/dL] / 2)
  // >4.68 = elevated insulin resistance; >5.0 = high
  tyg_elevated: 4.68,
  tyg_high: 5.0,

  // Omega-3 Index (% EPA+DHA in red blood cells)
  // <4% = high inflammatory/cardiovascular risk; 4–8% = suboptimal; >8% = optimal
  omega3_low: 4.0,
  omega3_suboptimal: 8.0,

  // ─── Body Composition ────────────────────────────────────────────────────────
  // BMI: WHO classification
  bmi_overweight: 25,
  bmi_obese: 30,
  // Body Fat %: sex-neutral thresholds (ACE guidelines midpoint)
  // Men: >25% elevated, >30% obese; Women: >32% elevated, >38% obese
  bodyFat_elevated: 28,   // sex-neutral midpoint
  bodyFat_high: 35,
  // VO2 Max: population norms (lower = worse cardiorespiratory fitness)
  // AHA/ACSM: <30 below average for most adults, 38+ fair-to-good
  vo2max_low: 30,
  vo2max_fair: 38,
};

// ─── Core grading functions ───────────────────────────────────────────────────

function gradeImmunometabolic(input: PhenotypeInput): PhenotypeResult {
  const signals: PhenotypeSignal[] = [];
  let totalWeight = 0;
  let weightedScore = 0;

  // --- Signal 1: Diet/appetite (inverse — low diet score = appetite dysregulation)
  // In immunometabolic phenotype, appetite typically INCREASES
  // Low diet rating can signal either increased or poor eating — we pair with symptom data
  if (input.pillars && input.pillars.diet >= 0) {
    const dietScore = input.pillars.diet;
    // Poor/Fair diet (0 or 1) contributes to this phenotype
    const contribution = dietScore <= 1 ? (2 - dietScore) / 2 : 0;
    signals.push({
      key: 'diet', label: 'Diet Quality', value: ['Poor', 'Fair', 'Good', 'Excellent'][dietScore],
      direction: dietScore <= 1 ? 'reduced' : 'normal', weight: contribution, source: 'Weekly Check-In',
    });
    totalWeight += 0.12;
    weightedScore += contribution * 0.12;
  }

  // --- Signal 2: Increased appetite (structured field or symptom fallback)
  {
    const hasHunger = input.appetite === 3
      || (input.symptoms?.includes('Increased appetite'))
      || (input.symptoms?.includes('Increased hunger')); // legacy label
    const hungerScore = hasHunger ? 1 : 0;
    const appetiteSource = input.appetite != null ? 'Check-In' : 'Symptom Report';
    signals.push({
      key: 'hunger', label: 'Increased Appetite', value: hasHunger ? 'Yes' : 'No',
      direction: hasHunger ? 'elevated' : 'normal', weight: hungerScore, source: appetiteSource,
    });
    totalWeight += 0.15;
    weightedScore += hungerScore * 0.15;

    if (input.symptoms?.includes('Bloating')) {
      signals.push({
        key: 'bloating', label: 'Bloating/GI', value: 'Yes',
        direction: 'elevated', weight: 0.6, source: 'Symptom Report',
      });
      totalWeight += 0.05;
      weightedScore += 0.6 * 0.05;
    }
  }

  // --- Signal 2b: Weight gain (confirmatory — Simmons 2020: appetite↑ MDD + weight gain)
  if (input.weightDelta != null) {
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (input.weightDelta > 3) { contribution = 1; dir = 'elevated'; }
    else if (input.weightDelta > 1) { contribution = 0.4; dir = 'elevated'; }
    signals.push({
      key: 'weight_gain', label: 'Weight Change (30d)',
      value: `${input.weightDelta > 0 ? '+' : ''}${input.weightDelta} lb`,
      direction: dir, weight: contribution, source: 'Apple Health',
    });
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal 3: hsCRP (inflammation — strongest biomarker)
  // Simmons et al. 2020: appetite↑ MDD = elevated CRP, IL-6, IL-1RA
  if (input.labs?.hsCRP != null) {
    const crp = input.labs.hsCRP;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (crp >= THRESHOLDS.hsCRP_high) { contribution = 1; dir = 'elevated'; }
    else if (crp >= THRESHOLDS.hsCRP_elevated) { contribution = 0.6; dir = 'elevated'; }
    signals.push({
      key: 'hsCRP', label: 'hsCRP (Inflammation)', value: `${crp} mg/L`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.25;
    weightedScore += contribution * 0.25;
  }

  // --- Signal 4: Insulin / HOMA-IR proxy
  // Simmons et al. 2020: appetite↑ MDD = higher insulin, HOMA-IR, leptin
  if (input.labs?.insulin != null) {
    const ins = input.labs.insulin;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (ins >= THRESHOLDS.insulin_high) { contribution = 1; dir = 'elevated'; }
    else if (ins >= THRESHOLDS.insulin_elevated) { contribution = 0.6; dir = 'elevated'; }
    signals.push({
      key: 'insulin', label: 'Fasting Insulin', value: `${ins} uIU/mL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.2;
    weightedScore += contribution * 0.2;
  }

  // --- Signal 5: Glucose / HbA1c (metabolic dysfunction)
  if (input.labs?.glucose != null) {
    const glu = input.labs.glucose;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (glu >= THRESHOLDS.glucose_high) { contribution = 1; dir = 'elevated'; }
    else if (glu >= THRESHOLDS.glucose_elevated) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'glucose', label: 'Fasting Glucose', value: `${glu} mg/dL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.1;
    weightedScore += contribution * 0.1;
  }

  if (input.labs?.hba1c != null) {
    const a1c = input.labs.hba1c;
    let contribution = a1c >= THRESHOLDS.hba1c_elevated ? 0.7 : 0;
    signals.push({
      key: 'hba1c', label: 'HbA1c', value: `${a1c}%`,
      direction: a1c >= THRESHOLDS.hba1c_elevated ? 'elevated' : 'normal',
      weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal 6: Fatigue + afternoon crash (common in immunometabolic)
  if (input.symptoms) {
    const hasFatigue = input.symptoms.includes('Fatigue');
    const hasCrash = input.symptoms.includes('Afternoon crash');
    const fatigueSig = (hasFatigue ? 0.5 : 0) + (hasCrash ? 0.5 : 0);
    if (hasFatigue || hasCrash) {
      signals.push({
        key: 'fatigue', label: 'Fatigue / Energy Dip',
        value: [hasFatigue && 'Fatigue', hasCrash && 'Afternoon crash'].filter(Boolean).join(', '),
        direction: 'elevated', weight: fatigueSig, source: 'Symptom Report',
      });
    }
    totalWeight += 0.05;
    weightedScore += fatigueSig * 0.05;
  }

  // --- Signal 7 (IMHC Tier 2): TyG Index — insulin resistance proxy
  // TyG = ln(fasting TG [mg/dL] × fasting glucose [mg/dL] / 2)
  // Stronger signal than either alone; doesn't require HOMA-IR calculation
  if (input.labs?.triglycerides != null && input.labs?.triglycerides > 0 &&
      input.labs?.glucose != null && input.labs?.glucose > 0) {
    const tyg = Math.log((input.labs.triglycerides * input.labs.glucose) / 2);
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (tyg >= THRESHOLDS.tyg_high) { contribution = 1; dir = 'elevated'; }
    else if (tyg >= THRESHOLDS.tyg_elevated) { contribution = 0.6; dir = 'elevated'; }
    signals.push({
      key: 'tyg', label: 'TyG Index (Insulin Resistance)',
      value: tyg.toFixed(2),
      direction: dir, weight: contribution, source: 'Lab Results (TG × Glucose)',
    });
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 8 (IMHC Tier 2): Omega-3 Index — anti-inflammatory status
  // Low omega-3 index associated with elevated neuroinflammation in depression
  if (input.labs?.omega3Index != null) {
    const o3 = input.labs.omega3Index;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (o3 < THRESHOLDS.omega3_low) { contribution = 1; dir = 'reduced'; }
    else if (o3 < THRESHOLDS.omega3_suboptimal) { contribution = 0.4; dir = 'reduced'; }
    signals.push({
      key: 'omega3', label: 'Omega-3 Index (EPA+DHA)',
      value: `${o3}%`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal 9 (IMHC Tier 1): Ferritin — iron stores / acute phase context
  // Elevated ferritin alongside elevated hsCRP = acute inflammatory reaction
  // Low ferritin = iron deficiency → fatigue overlap with immunometabolic
  if (input.labs?.ferritin != null) {
    const ferr = input.labs.ferritin;
    const crp = input.labs?.hsCRP ?? 0;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (ferr >= THRESHOLDS.ferritin_elevated && crp >= THRESHOLDS.hsCRP_elevated) {
      // Co-elevation = inflammatory acute phase pattern
      contribution = 0.8; dir = 'elevated';
    } else if (ferr <= THRESHOLDS.ferritin_low) {
      // Low ferritin = iron deficiency → fatigue/anergia overlap
      contribution = 0.5; dir = 'reduced';
    }
    if (contribution > 0) {
      signals.push({
        key: 'ferritin', label: 'Ferritin (Iron / Inflammation)',
        value: `${ferr} ng/mL`,
        direction: dir, weight: contribution, source: 'Lab Results',
      });
    }
    totalWeight += 0.06;
    weightedScore += contribution * 0.06;
  }

  // --- Signal: Body Fat Elevated (adiposity drives systemic inflammation — Milaneschi 2019/2020)
  if (input.bodyComposition?.bodyFat != null) {
    const bf = input.bodyComposition.bodyFat;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (bf >= THRESHOLDS.bodyFat_high) { contribution = 1; dir = 'elevated'; }
    else if (bf >= THRESHOLDS.bodyFat_elevated) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'body_fat', label: 'Body Fat %',
      value: `${bf}%`,
      direction: dir, weight: contribution, source: 'Apple Health',
    });
    totalWeight += 0.12;
    weightedScore += contribution * 0.12;
  }

  // --- Signal: BMI ≥ 30 (crude adiposity proxy — secondary to body fat)
  if (input.bodyComposition?.bmi != null) {
    const bmi = input.bodyComposition.bmi;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (bmi >= THRESHOLDS.bmi_obese) { contribution = 0.8; dir = 'elevated'; }
    else if (bmi >= THRESHOLDS.bmi_overweight) { contribution = 0.3; dir = 'elevated'; }
    signals.push({
      key: 'bmi', label: 'BMI',
      value: `${bmi}`,
      direction: dir, weight: contribution, source: 'Apple Health',
    });
    totalWeight += 0.06;
    weightedScore += contribution * 0.06;
  }

  // --- Signal: Body Fat Trending Up (worsening metabolic trajectory)
  if (input.bodyComposition?.bodyFatDelta != null) {
    const delta = input.bodyComposition.bodyFatDelta;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (delta > 2) { contribution = 1; dir = 'elevated'; }
    else if (delta > 0.5) { contribution = 0.4; dir = 'elevated'; }
    if (contribution > 0) {
      signals.push({
        key: 'body_fat_trend', label: 'Body Fat Trend (30d)',
        value: `+${delta.toFixed(1)}%`,
        direction: dir, weight: contribution, source: 'Apple Health',
      });
    }
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal: Low VO2 Max (deconditioning — correlates with metabolic syndrome)
  if (input.bodyComposition?.vo2Max != null) {
    const vo2 = input.bodyComposition.vo2Max;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (vo2 < THRESHOLDS.vo2max_low) { contribution = 0.8; dir = 'reduced'; }
    else if (vo2 < THRESHOLDS.vo2max_fair) { contribution = 0.3; dir = 'reduced'; }
    if (contribution > 0) {
      signals.push({
        key: 'vo2max', label: 'VO2 Max',
        value: `${vo2} mL/kg/min`,
        direction: dir, weight: contribution, source: 'Apple Health',
      });
    }
    totalWeight += 0.06;
    weightedScore += contribution * 0.06;
  }

  // Normalize score to 0–100. Confidence = totalWeight ÷ MAX_WEIGHT, where
  // MAX_WEIGHT is the sum of every conditional weight increment in this
  // function when all inputs are present. Derived empirically by summing
  // every `totalWeight += X` above:
  //   0.12 + 0.15 + 0.05 + 0.08 + 0.25 + 0.20 + 0.10 + 0.08 + 0.05
  // + 0.10 + 0.08 + 0.06 + 0.12 + 0.06 + 0.08 + 0.06 = 1.64
  // A test in phenotype.test.ts re-derives this from full-data input and
  // asserts confidence === 100, catching drift if weights change.
  const MAX_WEIGHT = 1.64;
  const rawScore = totalWeight > 0 ? (weightedScore / totalWeight) * 100 : 0;
  const score = Math.round(Math.min(100, rawScore));
  const confidence = Math.round(Math.min(100, (totalWeight / MAX_WEIGHT) * 100));

  const clinicalNotes: string[] = [];
  if (score >= 50) {
    clinicalNotes.push('Immune-metabolic pattern indicates inflammation and metabolic dysregulation as primary biological pathways.');
    clinicalNotes.push('In clinical literature, this pattern is commonly observed in presentations involving atypical features with appetite increase and fatigue (Milaneschi et al. 2020, Lamers et al. 2020).');
  }
  if (input.labs?.hsCRP && input.labs.hsCRP >= THRESHOLDS.hsCRP_high) {
    clinicalNotes.push('hsCRP >3.0 mg/L is associated with the appetite-increase MDD subtype (Simmons et al. 2020) and distinguishes inflammatory from non-inflammatory presentations.');
  }
  if (input.labs?.omega3Index != null && input.labs.omega3Index < THRESHOLDS.omega3_low) {
    clinicalNotes.push('Omega-3 Index <4% suggests low EPA+DHA status, which may amplify neuroinflammatory signaling (Sublette et al., 2011).');
  }
  if (input.labs?.ferritin != null && input.labs.ferritin <= THRESHOLDS.ferritin_low) {
    clinicalNotes.push('Low ferritin is independently associated with fatigue and depression severity (Botturi et al., 2024).');
  }

  return {
    id: 'immunometabolic',
    name: 'Inflammation & Metabolism',
    shortName: 'Immune-Metabolic',
    score,
    confidence,
    level: score >= 60 ? 'strong' : score >= 40 ? 'moderate' : score >= 20 ? 'mild' : 'minimal',
    color: '#EF4444',
    description: 'Your data shows patterns consistent with elevated inflammatory and metabolic signaling — immune markers, insulin regulation, appetite changes, and energy disruption. These patterns reflect immunometabolic system activity.',
    signals: signals.filter(s => s.weight > 0 || s.direction !== 'normal'),
    clinicalNotes,
  };
}

function gradeHPADominant(input: PhenotypeInput): PhenotypeResult {
  const signals: PhenotypeSignal[] = [];
  let totalWeight = 0;
  let weightedScore = 0;

  // --- Signal 1: Stress management (inverse — low score = HPA overactivation)
  if (input.pillars && input.pillars.stress >= 0) {
    const stressScore = input.pillars.stress;
    // Overwhelmed (0) or Struggling (1) = high HPA signal
    const contribution = stressScore <= 1 ? (2 - stressScore) / 2 : 0;
    signals.push({
      key: 'stress_mgmt', label: 'Stress Management',
      value: ['Overwhelmed', 'Struggling', 'Managing', 'Thriving'][stressScore],
      direction: stressScore <= 1 ? 'reduced' : 'normal', weight: contribution, source: 'Weekly Check-In',
    });
    totalWeight += 0.15;
    weightedScore += contribution * 0.15;
  }

  // --- Signal 2: Cortisol (direct HPA marker)
  // Simmons et al. 2020: appetite↓ MDD = significantly elevated cortisol
  if (input.labs?.cortisol != null) {
    const cort = input.labs.cortisol;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (cort >= THRESHOLDS.cortisol_elevated) { contribution = 1; dir = 'elevated'; }
    else if (cort >= 16) { contribution = 0.4; dir = 'elevated'; }
    signals.push({
      key: 'cortisol', label: 'Cortisol', value: `${cort} ug/dL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.25;
    weightedScore += contribution * 0.25;
  }

  // --- Signal 3: Wearable stress resilience (low = chronic stress activation)
  if (input.wearables?.avgStressScore != null) {
    const stress = input.wearables.avgStressScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (stress < 40) { contribution = 1; dir = 'elevated'; }
    else if (stress < 60) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'wearable_stress', label: 'Physiological Stress',
      value: `${stress}/100 resilience`,
      direction: dir, weight: contribution, source: 'Oura Ring',
    });
    totalWeight += 0.15;
    weightedScore += contribution * 0.15;
  }

  // --- Signal 4: Sleep disruption (hyperarousal → poor sleep)
  if (input.wearables?.avgSleepScore != null) {
    const sleep = input.wearables.avgSleepScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (sleep < THRESHOLDS.sleep_poor) { contribution = 0.8; dir = 'reduced'; }
    else if (sleep < THRESHOLDS.sleep_fair) { contribution = 0.4; dir = 'reduced'; }
    signals.push({
      key: 'sleep_quality', label: 'Sleep Quality',
      value: `${sleep}/100`,
      direction: dir, weight: contribution, source: 'Oura Ring',
    });
    totalWeight += 0.1;
    weightedScore += contribution * 0.1;
  }

  // --- Signal 5: Poor sleep symptom
  if (input.symptoms) {
    const poorSleep = input.symptoms.includes('Poor sleep');
    const nightSweats = input.symptoms.includes('Night sweats');
    const sleepSig = (poorSleep ? 0.6 : 0) + (nightSweats ? 0.4 : 0);
    if (poorSleep || nightSweats) {
      signals.push({
        key: 'sleep_symptoms', label: 'Sleep Disturbance',
        value: [poorSleep && 'Poor sleep', nightSweats && 'Night sweats'].filter(Boolean).join(', '),
        direction: 'elevated', weight: sleepSig, source: 'Symptom Report',
      });
    }
    totalWeight += 0.08;
    weightedScore += sleepSig * 0.08;
  }

  // --- Signal 6: Anxiety from PHQ-4 (q3+q4 — anxious arousal feeds HPA)
  if (input.phq4) {
    const anxScore = input.phq4.q3 + input.phq4.q4;  // 0–6
    const contribution = Math.min(1, anxScore / 4);  // Scale to 0–1
    signals.push({
      key: 'anxiety', label: 'Anxiety Subscale (PHQ-4)',
      value: `${anxScore}/6`,
      direction: anxScore >= 3 ? 'elevated' : 'normal', weight: contribution, source: 'PHQ-4',
    });
    totalWeight += 0.12;
    weightedScore += contribution * 0.12;
  }

  // --- Signal 7: Irritability / mood dysregulation
  if (input.symptoms) {
    const hasIrritability = input.symptoms.includes('Irritability');
    const hasMoodSwings = input.symptoms.includes('Mood swings');
    const moodSig = (hasIrritability ? 0.5 : 0) + (hasMoodSwings ? 0.5 : 0);
    if (hasIrritability || hasMoodSwings) {
      signals.push({
        key: 'irritability', label: 'Mood Dysregulation',
        value: [hasIrritability && 'Irritability', hasMoodSwings && 'Mood swings'].filter(Boolean).join(', '),
        direction: 'elevated', weight: moodSig, source: 'Symptom Report',
      });
    }
    totalWeight += 0.08;
    weightedScore += moodSig * 0.08;
  }

  // --- Signal 7b: Decreased appetite (Simmons 2020 — appetite↓ MDD = HPA/cortisol subtype)
  {
    const hasDecreasedAppetite = (input.appetite != null && input.appetite <= 1)
      || (input.symptoms?.includes('Decreased appetite'));
    const appetiteSource = input.appetite != null ? 'Check-In' : 'Symptom Report';
    signals.push({
      key: 'decreased_appetite', label: 'Decreased Appetite',
      value: hasDecreasedAppetite ? 'Yes' : 'No',
      direction: hasDecreasedAppetite ? 'elevated' : 'normal', weight: hasDecreasedAppetite ? 1 : 0, source: appetiteSource,
    });
    totalWeight += 0.12;
    weightedScore += (hasDecreasedAppetite ? 1 : 0) * 0.12;
  }

  // --- Signal 7c: Weight loss (confirmatory — Simmons 2020: appetite↓ MDD + weight loss)
  if (input.weightDelta != null) {
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (input.weightDelta < -3) { contribution = 1; dir = 'reduced'; }
    else if (input.weightDelta < -1) { contribution = 0.4; dir = 'reduced'; }
    signals.push({
      key: 'weight_loss', label: 'Weight Change (30d)',
      value: `${input.weightDelta > 0 ? '+' : ''}${input.weightDelta} lb`,
      direction: dir, weight: contribution, source: 'Apple Health',
    });
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal 7d: Catabolic compound signal (rapid weight loss + declining fitness = stress catabolism)
  if (input.weightDelta != null && input.weightDelta < -3
      && input.bodyComposition?.vo2MaxDelta != null && input.bodyComposition.vo2MaxDelta < -2) {
    signals.push({
      key: 'catabolic', label: 'Catabolic Pattern (Weight + Fitness Decline)',
      value: `${input.weightDelta} lb / ${input.bodyComposition.vo2MaxDelta.toFixed(1)} VO2`,
      direction: 'reduced', weight: 1, source: 'Apple Health',
    });
    totalWeight += 0.06;
    weightedScore += 1 * 0.06;
  }

  // --- Signal 8: Low HRV (autonomic imbalance — HPA hyperactivation)
  if (input.wearables?.avgHRV != null) {
    const hrv = input.wearables.avgHRV;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (hrv < THRESHOLDS.hrv_low) { contribution = 1; dir = 'reduced'; }
    else if (hrv < THRESHOLDS.hrv_moderate) { contribution = 0.4; dir = 'reduced'; }
    signals.push({
      key: 'hrv', label: 'Heart Rate Variability',
      value: `${hrv} ms`,
      direction: dir, weight: contribution, source: 'Oura Ring',
    });
    totalWeight += 0.07;
    weightedScore += contribution * 0.07;
  }

  // --- Signal 9 (IMHC Tier 1): TSH — thyroid-HPA overlap
  // Subclinical hypothyroid (TSH >2.5) can mimic and amplify HPA dysregulation
  // Must be evaluated as a Tier 1 rule-out before attributing purely to HPA
  if (input.labs?.tsh != null) {
    const tsh = input.labs.tsh;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (tsh >= THRESHOLDS.tsh_elevated) { contribution = 0.8; dir = 'elevated'; }
    else if (tsh >= THRESHOLDS.tsh_subclinical) { contribution = 0.4; dir = 'elevated'; }
    signals.push({
      key: 'tsh', label: 'TSH (Thyroid)',
      value: `${tsh} mIU/L`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  // --- Signal 10: Rumination & worry (RRS/PSWQ brief — chronic HPA activation via ruminative thought)
  // Watkins 2018 — rumination as mood-cued mental habit sustaining HPA dysregulation
  // Brewer & Roy 2021 — worry as habit loop maintaining sympathetic arousal
  if (input.checkin?.rumScore != null) {
    const rum = input.checkin.rumScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (rum >= 6)      { contribution = 1.0; dir = 'elevated'; }
    else if (rum >= 3) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'rumination', label: 'Rumination & Worry',
      value: `${rum}/9`,
      direction: dir, weight: contribution, source: 'Check-In',
    });
    totalWeight += 0.12;
    weightedScore += contribution * 0.12;
  }

  const rawScore = totalWeight > 0 ? (weightedScore / totalWeight) * 100 : 0;
  const score = Math.round(Math.min(100, rawScore));
  // MAX_WEIGHT excludes the catabolic compound signal (0.06) because it is
  // a derived bonus that fires only when weightDelta < -3 AND vo2MaxDelta < -2
  // simultaneously — and its inputs are already counted by the weight-loss
  // and HRV branches. Including it would double-count those inputs and make
  // confidence cap below 100 for non-catabolic HPA presentations.
  //   0.15 + 0.25 + 0.15 + 0.10 + 0.08 + 0.12 + 0.08 + 0.12 + 0.08
  // + 0.07 + 0.08 + 0.12 = 1.40   (excluding catabolic 0.06)
  // See phenotype.test.ts for the full-data invariant test.
  const MAX_WEIGHT_HPA = 1.40;
  const confidence = Math.round(Math.min(100, (totalWeight / MAX_WEIGHT_HPA) * 100));

  const clinicalNotes: string[] = [];
  if (score >= 50) {
    clinicalNotes.push('Stress-response pattern indicates chronic stress axis overactivation with cortisol dysregulation as the primary biological mechanism.');
    clinicalNotes.push('In clinical literature, this pattern is commonly observed in melancholic presentations with appetite suppression and weight loss (Simmons et al. 2020, Gold 2015).');
  }
  if (input.labs?.tsh != null && input.labs.tsh >= THRESHOLDS.tsh_elevated) {
    clinicalNotes.push('TSH ≥4.0 mIU/L — thyroid dysfunction should be evaluated and corrected before attributing symptoms to HPA axis alone (IMHC Tier 1 rule-out).');
  } else if (input.labs?.tsh != null && input.labs.tsh >= THRESHOLDS.tsh_subclinical) {
    clinicalNotes.push('TSH in subclinical range (2.5–4.0) may contribute to mood and stress regulation symptoms. Thyroid function should be evaluated as a potential contributing factor.');
  }

  return {
    id: 'hpa_dominant',
    name: 'Stress Response',
    shortName: 'HPA / Stress',
    score,
    confidence,
    level: score >= 60 ? 'strong' : score >= 40 ? 'moderate' : score >= 20 ? 'mild' : 'minimal',
    color: '#F59E0B',
    description: 'Your data shows patterns consistent with chronic stress-response activation — HPA axis signaling, cortisol regulation, sleep disruption, and autonomic imbalance. These patterns reflect stress-axis system activity.',
    signals: signals.filter(s => s.weight > 0 || s.direction !== 'normal'),
    clinicalNotes,
  };
}

function gradeAnhedonic(input: PhenotypeInput): PhenotypeResult {
  const signals: PhenotypeSignal[] = [];
  let totalWeight = 0;
  let weightedScore = 0;

  // --- Signal 1: PHQ-4 q1 — interest/pleasure (THE anhedonia question)
  // Pizzagalli 2014: Anhedonia = blunted reward responsiveness, core endophenotype
  if (input.phq4) {
    const q1 = input.phq4.q1;
    const contribution = q1 / 3;  // 0–1
    signals.push({
      key: 'anhedonia', label: 'Loss of Interest/Pleasure',
      value: ['Not at all', 'Several days', 'More than half', 'Nearly every day'][q1],
      direction: q1 >= 2 ? 'elevated' : q1 >= 1 ? 'elevated' : 'normal',
      weight: contribution, source: 'PHQ-4 Q1',
    });
    totalWeight += 0.30;
    weightedScore += contribution * 0.30;
  }

  // --- Signal 2: Low motivation symptom
  if (input.symptoms) {
    const hasLowMot = input.symptoms.includes('Low motivation');
    signals.push({
      key: 'motivation', label: 'Low Motivation',
      value: hasLowMot ? 'Yes' : 'No',
      direction: hasLowMot ? 'elevated' : 'normal',
      weight: hasLowMot ? 1 : 0, source: 'Symptom Report',
    });
    totalWeight += 0.15;
    weightedScore += (hasLowMot ? 1 : 0) * 0.15;
  }

  // --- Signal 3: Social disconnection (withdrawal from reward-seeking)
  if (input.pillars && input.pillars.social >= 0) {
    const social = input.pillars.social;
    // Isolated (0) or Disconnected (1) = social anhedonia signal
    const contribution = social <= 1 ? (2 - social) / 2 : 0;
    signals.push({
      key: 'social', label: 'Social Connection',
      value: ['Isolated', 'Disconnected', 'Connected', 'Thriving'][social],
      direction: social <= 1 ? 'reduced' : 'normal', weight: contribution, source: 'Weekly Check-In',
    });
    totalWeight += 0.15;
    weightedScore += contribution * 0.15;
  }

  // --- Signal 4: Brain fog (cognitive blunting — related to DA downregulation)
  if (input.symptoms) {
    const hasFog = input.symptoms.includes('Brain fog');
    signals.push({
      key: 'brainfog', label: 'Brain Fog',
      value: hasFog ? 'Yes' : 'No',
      direction: hasFog ? 'elevated' : 'normal',
      weight: hasFog ? 1 : 0, source: 'Symptom Report',
    });
    totalWeight += 0.10;
    weightedScore += (hasFog ? 1 : 0) * 0.10;
  }

  // --- Signal 5: Fatigue (energy deficit — overlaps but distinct pattern)
  if (input.symptoms) {
    const hasFatigue = input.symptoms.includes('Fatigue');
    if (hasFatigue) {
      signals.push({
        key: 'fatigue', label: 'Fatigue',
        value: 'Yes',
        direction: 'elevated', weight: 0.6, source: 'Symptom Report',
      });
    }
    totalWeight += 0.08;
    weightedScore += (hasFatigue ? 0.6 : 0) * 0.08;
  }

  // --- Signal 6: PHQ-4 q2 — feeling down (core depression, amplifies anhedonia)
  if (input.phq4) {
    const q2 = input.phq4.q2;
    const contribution = q2 / 3;
    signals.push({
      key: 'depressed_mood', label: 'Depressed Mood',
      value: ['Not at all', 'Several days', 'More than half', 'Nearly every day'][q2],
      direction: q2 >= 2 ? 'elevated' : 'normal',
      weight: contribution, source: 'PHQ-4 Q2',
    });
    totalWeight += 0.12;
    weightedScore += contribution * 0.12;
  }

  // --- Signal 7: Low libido (reward/pleasure circuitry)
  if (input.symptoms) {
    const hasLowLibido = input.symptoms.includes('Low libido');
    if (hasLowLibido) {
      signals.push({
        key: 'libido', label: 'Low Libido',
        value: 'Yes',
        direction: 'elevated', weight: 0.7, source: 'Symptom Report',
      });
    }
    totalWeight += 0.05;
    weightedScore += (hasLowLibido ? 0.7 : 0) * 0.05;
  }

  // --- Signal 8: Readiness (low readiness may reflect reward-motivation deficit)
  if (input.wearables?.avgReadiness != null) {
    const ready = input.wearables.avgReadiness;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (ready < 60) { contribution = 0.6; dir = 'reduced'; }
    else if (ready < 72) { contribution = 0.3; dir = 'reduced'; }
    if (contribution > 0) {
      signals.push({
        key: 'readiness', label: 'Readiness Score',
        value: `${ready}/100`,
        direction: dir, weight: contribution, source: 'Oura Ring',
      });
    }
    totalWeight += 0.05;
    weightedScore += contribution * 0.05;
  }

  // --- Signal 9 (IMHC Tier 1): Vitamin D — deficiency strongly associated with anhedonia/fatigue
  if (input.labs?.vitaminD != null) {
    const vd = input.labs.vitaminD;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (vd < THRESHOLDS.vitaminD_deficient) { contribution = 1; dir = 'reduced'; }
    else if (vd < THRESHOLDS.vitaminD_insufficient) { contribution = 0.5; dir = 'reduced'; }
    signals.push({
      key: 'vitaminD', label: 'Vitamin D (25-OH)',
      value: `${vd} ng/mL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 10 (IMHC Tier 1): B12 — deficiency overlaps with cognitive blunting and fatigue
  if (input.labs?.b12 != null) {
    const b12 = input.labs.b12;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (b12 < THRESHOLDS.b12_deficient) { contribution = 1; dir = 'reduced'; }
    else if (b12 < THRESHOLDS.b12_low) { contribution = 0.4; dir = 'reduced'; }
    signals.push({
      key: 'b12', label: 'Vitamin B12',
      value: `${b12} pg/mL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.07;
    weightedScore += contribution * 0.07;
  }

  // --- Signal 11 (IMHC Tier 1): Folate — methylation and cognitive overlap with B12
  if (input.labs?.folate != null) {
    const fol = input.labs.folate;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (fol < THRESHOLDS.folate_deficient) { contribution = 1; dir = 'reduced'; }
    else if (fol < THRESHOLDS.folate_low) { contribution = 0.5; dir = 'reduced'; }
    signals.push({
      key: 'folate', label: 'Folate',
      value: `${fol} ng/mL`,
      direction: dir, weight: contribution, source: 'Lab Results',
    });
    totalWeight += 0.06;
    weightedScore += contribution * 0.06;
  }

  // --- Signal 12: Behavioral avoidance (BADS brief — withdrawal from reward-seeking activity)
  // Berg 2023 — behavioral avoidance is the core maintenance mechanism of anhedonic depression
  if (input.checkin?.badsAvoidance != null) {
    const avoidance = input.checkin.badsAvoidance;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (avoidance >= 2)      { contribution = 1.0; dir = 'elevated'; }
    else if (avoidance >= 1) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'bads_avoidance', label: 'Behavioral Avoidance',
      value: `${avoidance}/3`,
      direction: dir, weight: contribution, source: 'Check-In',
    });
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 13: Low behavioral activation (BADS brief — reduced reward engagement)
  if (input.checkin?.badsActivation != null) {
    const activation = input.checkin.badsActivation;
    // 0–6 scale: lower = less activated = stronger anhedonia signal
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (activation <= 1)     { contribution = 1.0; dir = 'reduced'; }
    else if (activation <= 3) { contribution = 0.4; dir = 'reduced'; }
    if (contribution > 0) {
      signals.push({
        key: 'bads_activation', label: 'Behavioral Activation',
        value: `${activation}/6`,
        direction: dir, weight: contribution, source: 'Check-In',
      });
    }
    totalWeight += 0.08;
    weightedScore += contribution * 0.08;
  }

  const rawScore = totalWeight > 0 ? (weightedScore / totalWeight) * 100 : 0;
  const score = Math.round(Math.min(100, rawScore));
  // MAX_WEIGHT derived by summing every conditional weight increment above:
  //   0.30 + 0.15 + 0.15 + 0.10 + 0.08 + 0.12 + 0.05 + 0.05 + 0.10
  // + 0.07 + 0.06 + 0.10 + 0.08 = 1.41
  // See phenotype.test.ts for the full-data invariant test.
  const MAX_WEIGHT_ANHEDONIC = 1.41;
  const confidence = Math.round(Math.min(100, (totalWeight / MAX_WEIGHT_ANHEDONIC) * 100));

  const clinicalNotes: string[] = [];
  if (score >= 50) {
    clinicalNotes.push('Reward-circuit pattern indicates blunted dopaminergic function as the primary biological mechanism (Pizzagalli 2014).');
    clinicalNotes.push('In clinical literature, this pattern is commonly observed in presentations involving anhedonia and motivational deficits (Treadway & Zald 2011).');
  }
  if (input.labs?.vitaminD != null && input.labs.vitaminD < THRESHOLDS.vitaminD_deficient) {
    clinicalNotes.push('Vitamin D deficiency (<20 ng/mL) is independently associated with depression severity and seasonal mood patterns.');
  }
  if (input.labs?.b12 != null && input.labs.b12 < THRESHOLDS.b12_deficient) {
    clinicalNotes.push('B12 deficiency may contribute to cognitive and fatigue symptoms that overlap with anhedonic presentation.');
  }
  if (input.labs?.folate != null && input.labs.folate < THRESHOLDS.folate_low) {
    clinicalNotes.push('Low folate is relevant to methylation pathways and cognitive function; often co-occurs with B12 deficiency.');
  }

  return {
    id: 'anhedonic',
    name: 'Reward & Motivation',
    shortName: 'Reward Circuit',
    score,
    confidence,
    level: score >= 60 ? 'strong' : score >= 40 ? 'moderate' : score >= 20 ? 'mild' : 'minimal',
    color: '#8B5CF6',
    description: 'Your data shows patterns consistent with reduced reward-circuit activation — diminished interest and pleasure, lower motivation, social withdrawal, and cognitive slowing. These patterns reflect dopaminergic reward-system activity.',
    signals: signals.filter(s => s.weight > 0 || s.direction !== 'normal'),
    clinicalNotes,
  };
}

function gradeAnxious(input: PhenotypeInput): PhenotypeResult {
  const signals: PhenotypeSignal[] = [];
  let totalWeight = 0;
  let weightedScore = 0;

  // --- Signal 1: PHQ-4 anxiety subscale (q3 + q4 — THE anxiety measure)
  if (input.phq4) {
    const anxScore = input.phq4.q3 + input.phq4.q4;  // 0–6
    const contribution = Math.min(1, anxScore / 4);
    signals.push({
      key: 'anxiety_phq4', label: 'Anxiety Subscale (PHQ-4)',
      value: `${anxScore}/6`,
      direction: anxScore >= 3 ? 'elevated' : anxScore >= 2 ? 'elevated' : 'normal',
      weight: contribution, source: 'PHQ-4 Q3+Q4',
    });
    totalWeight += 0.30;
    weightedScore += contribution * 0.30;
  }

  // --- Signal 2: Anxiety symptom
  if (input.symptoms) {
    const hasAnxiety = input.symptoms.includes('Anxiety');
    signals.push({
      key: 'anxiety_symptom', label: 'Self-Reported Anxiety',
      value: hasAnxiety ? 'Yes' : 'No',
      direction: hasAnxiety ? 'elevated' : 'normal',
      weight: hasAnxiety ? 1 : 0, source: 'Symptom Report',
    });
    totalWeight += 0.12;
    weightedScore += (hasAnxiety ? 1 : 0) * 0.12;
  }

  // --- Signal 3: Low HRV (autonomic dysregulation — vagal tone)
  if (input.wearables?.avgHRV != null) {
    const hrv = input.wearables.avgHRV;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (hrv < THRESHOLDS.hrv_low) { contribution = 1; dir = 'reduced'; }
    else if (hrv < THRESHOLDS.hrv_moderate) { contribution = 0.5; dir = 'reduced'; }
    signals.push({
      key: 'hrv', label: 'Heart Rate Variability',
      value: `${hrv} ms`,
      direction: dir, weight: contribution, source: 'Oura Ring',
    });
    totalWeight += 0.15;
    weightedScore += contribution * 0.15;
  }

  // --- Signal 4: Poor sleep (anxiety → insomnia cycle)
  if (input.wearables?.avgSleepScore != null) {
    const sleep = input.wearables.avgSleepScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (sleep < THRESHOLDS.sleep_poor) { contribution = 0.8; dir = 'reduced'; }
    else if (sleep < THRESHOLDS.sleep_fair) { contribution = 0.4; dir = 'reduced'; }
    if (contribution > 0) {
      signals.push({
        key: 'sleep', label: 'Sleep Quality',
        value: `${sleep}/100`,
        direction: dir, weight: contribution, source: 'Oura Ring',
      });
    }
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 5: Stress resilience (wearable)
  if (input.wearables?.avgStressScore != null) {
    const stress = input.wearables.avgStressScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (stress < 40) { contribution = 0.8; dir = 'elevated'; }
    else if (stress < 60) { contribution = 0.4; dir = 'elevated'; }
    if (contribution > 0) {
      signals.push({
        key: 'physio_stress', label: 'Physiological Stress',
        value: `${stress}/100 resilience`,
        direction: dir, weight: contribution, source: 'Oura Ring',
      });
    }
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 6: Stress management self-report
  if (input.pillars && input.pillars.stress >= 0) {
    const stressScore = input.pillars.stress;
    const contribution = stressScore <= 1 ? (2 - stressScore) / 2 : 0;
    signals.push({
      key: 'stress_report', label: 'Self-Reported Stress',
      value: ['Overwhelmed', 'Struggling', 'Managing', 'Thriving'][stressScore],
      direction: stressScore <= 1 ? 'elevated' : 'normal', weight: contribution, source: 'Weekly Check-In',
    });
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  // --- Signal 7: Irritability (anxious agitation)
  if (input.symptoms) {
    const hasIrritability = input.symptoms.includes('Irritability');
    if (hasIrritability) {
      signals.push({
        key: 'irritability', label: 'Irritability',
        value: 'Yes',
        direction: 'elevated', weight: 0.7, source: 'Symptom Report',
      });
    }
    totalWeight += 0.05;
    weightedScore += (hasIrritability ? 0.7 : 0) * 0.05;
  }

  // --- Signal 8: Headache (stress-related somatic symptom)
  if (input.symptoms) {
    const hasHeadache = input.symptoms.includes('Headache');
    if (hasHeadache) {
      signals.push({
        key: 'headache', label: 'Headache',
        value: 'Yes',
        direction: 'elevated', weight: 0.5, source: 'Symptom Report',
      });
    }
    totalWeight += 0.03;
    weightedScore += (hasHeadache ? 0.5 : 0) * 0.03;
  }

  // --- Signal 9: Habitual negative thinking (HINT brief — automatic, cue-driven neg thought patterns)
  // Colvin 2021 — HINT validated measure of thought habit automaticity and cue-dependence
  // Brewer & Roy 2021 — anxiety as habit loop; automaticity distinguishes anxiety disorder from normal worry
  if (input.checkin?.hintScore != null) {
    const hint = input.checkin.hintScore;
    let contribution = 0;
    let dir: PhenotypeSignal['direction'] = 'normal';
    if (hint >= 6)      { contribution = 1.0; dir = 'elevated'; }
    else if (hint >= 3) { contribution = 0.5; dir = 'elevated'; }
    signals.push({
      key: 'hint', label: 'Habitual Negative Thinking',
      value: `${hint}/9`,
      direction: dir, weight: contribution, source: 'Check-In',
    });
    totalWeight += 0.10;
    weightedScore += contribution * 0.10;
  }

  const rawScore = totalWeight > 0 ? (weightedScore / totalWeight) * 100 : 0;
  const score = Math.round(Math.min(100, rawScore));
  // MAX_WEIGHT derived by summing every conditional weight increment above:
  //   0.30 + 0.12 + 0.15 + 0.10 + 0.10 + 0.10 + 0.05 + 0.03 + 0.10 = 1.05
  // See phenotype.test.ts for the full-data invariant test.
  const MAX_WEIGHT_ANXIOUS = 1.05;
  const confidence = Math.round(Math.min(100, (totalWeight / MAX_WEIGHT_ANXIOUS) * 100));

  const clinicalNotes: string[] = [];
  if (score >= 50) {
    clinicalNotes.push('Nervous system recovery pattern indicates autonomic dysregulation (low HRV) and elevated sympathetic tone as primary biological mechanisms.');
    clinicalNotes.push('In clinical literature, this pattern is commonly observed in presentations involving anxious distress (Ionescu et al. 2015, Gaspersz et al. 2018).');
  }

  return {
    id: 'anxious',
    name: 'Nervous System & Recovery',
    shortName: 'Autonomic / ANS',
    score,
    confidence,
    level: score >= 60 ? 'strong' : score >= 40 ? 'moderate' : score >= 20 ? 'mild' : 'minimal',
    color: '#1E6FBF',
    description: 'Your data shows patterns consistent with elevated sympathetic nervous system activity — autonomic imbalance, disrupted sleep architecture, somatic tension, and heightened threat-response signaling. Heart rate variability is a key marker tracked in this pattern.',
    signals: signals.filter(s => s.weight > 0 || s.direction !== 'normal'),
    clinicalNotes,
  };
}

// ─── Main grading function ────────────────────────────────────────────────────

export function gradePhenotypes(input: PhenotypeInput): PhenotypeProfile {
  // Calculate PHQ-4 total
  const phq4Score = input.phq4
    ? input.phq4.q1 + input.phq4.q2 + input.phq4.q3 + input.phq4.q4
    : 0;

  // Determine screening level (standard PHQ-4 cutoffs)
  let screeningLevel: PhenotypeProfile['screeningLevel'] = 'normal';
  if (phq4Score >= 9) screeningLevel = 'severe';
  else if (phq4Score >= 6) screeningLevel = 'moderate';
  else if (phq4Score >= 3) screeningLevel = 'mild';

  // Grade all four phenotypes.
  //
  // Tie-break: when two phenotypes share an identical score, prefer the one
  // earlier in TIE_BREAK_ORDER. ES2019+ Array.prototype.sort is stable, so
  // input order would already produce deterministic ties — but encoding the
  // priority explicitly makes the contract refactor-safe (reordering the
  // input array no longer silently changes which phenotype "wins" a tie).
  const TIE_BREAK_ORDER = ['immunometabolic', 'hpa_dominant', 'anhedonic', 'anxious'] as const;
  const results = [
    gradeImmunometabolic(input),
    gradeHPADominant(input),
    gradeAnhedonic(input),
    gradeAnxious(input),
  ].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return TIE_BREAK_ORDER.indexOf(a.id as typeof TIE_BREAK_ORDER[number])
         - TIE_BREAK_ORDER.indexOf(b.id as typeof TIE_BREAK_ORDER[number]);
  });

  // Determine primary phenotype (highest score, only if PHQ-4 ≥ 3)
  const primary = phq4Score >= 3 && results[0].score >= 15 ? results[0] : null;

  // Calculate data completeness
  const missingData: string[] = [];
  const dataPoints = {
    'PHQ-4 Check-In': !!input.phq4,
    'Lifestyle Pillars': !!input.pillars,
    'Symptom Report': input.symptoms && input.symptoms.length > 0,
    'Lab Biomarkers': !!input.labs && Object.values(input.labs).some(v => v != null),
    'Wearable Data': !!input.wearables && Object.values(input.wearables).some(v => v != null),
  };
  Object.entries(dataPoints).forEach(([label, present]) => {
    if (!present) missingData.push(label);
  });
  const availableCount = Object.values(dataPoints).filter(Boolean).length;
  const dataCompleteness = Math.round((availableCount / Object.keys(dataPoints).length) * 100);

  return {
    primary,
    results,
    dataCompleteness,
    missingData,
    screeningLevel,
    phq4Score,
    lastUpdated: new Date().toISOString(),
  };
}
